import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { Block, ItemLog } from '@goaltree/backend/convex/lib/sessionPlan'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useMutation, useQuery } from 'convex/react'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Pause,
  Play,
  Share2,
  SkipForward,
  Square,
  TrendingUp,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useCoachPersona } from '@/components/ask-coach'
import { useWakeLock } from '@/components/breath-pacer'
import { CoachAvatar } from '@/components/coach-avatar'
import { MoveFigure } from '@/components/move-figure'
import { TimerRing, clock, useNow } from '@/components/number-habit'
import {
  type Runner,
  SetRows,
  lastLine,
  logTarget,
  phaseLabel,
  phaseMs,
  targetLine,
  timerLeft,
} from '@/components/session-runner'
import { openShareCard } from '@/lib/share-card'

// Today's session over the whole screen (like the breath pacer): a grid of
// exercise cards with their movement figures; tap one to work through its sets.
// Close minimises back to the habit's sheet and the session stays open.

const btn =
  'inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-full text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50'

const itemDone = (l: ItemLog) => l.sets.every((s) => s.done)

export function SessionFullscreen({
  runner,
  title,
  habitId,
  onMinimize,
}: {
  runner: Runner
  title: string
  habitId: Id<'nodes'>
  onMinimize: () => void
}) {
  const { t } = useTranslation()
  const { voice } = useCoachPersona()
  const pepTalk = useAction(api.sessionPlansNode.pepTalk)
  const [pep, setPep] = useState<string | null>(null)
  const [sid, setSid] = useState(runner.session?._id ?? null)
  const view = useQuery(api.sessionPlans.getPlan, { habitId })
  const debrief = view?.session?._id === sid ? (view?.session?.debrief ?? null) : null
  const [focusId, setFocusId] = useState<string | null>(null)
  const [name, setName] = useState(runner.session?.sessionName ?? '')
  const tick = useNow(!runner.finished, 1000)
  const session = runner.session
  const items = runner.items
  useWakeLock(!runner.finished)

  const [startedAt, setStartedAt] = useState(session?.startedAt ?? Date.now())
  const [count, setCount] = useState({ done: 0, total: 0 })
  const done = items.filter(itemDone).length
  useEffect(() => {
    if (!session) return
    setSid(session._id)
    setName(session.sessionName)
    setStartedAt(session.startedAt)
    setCount({ done, total: session.items.length })
  }, [session, done])

  useEffect(() => {
    if (runner.flow && runner.timer) setFocusId(runner.timer.itemId)
  }, [runner.flow, runner.timer])

  const pepRef = useRef(pepTalk)
  pepRef.current = pepTalk
  useEffect(() => {
    if (!sid) return
    let live = true
    void pepRef
      .current({ sessionId: sid })
      .then((line) => live && setPep(line))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [sid])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMinimize()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onMinimize])

  const focused = items.find((l) => l.itemId === focusId) ?? null
  const elapsed = Math.max(0, tick - startedAt)

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={name || t('sessionPlan.today')}
      data-testid="session-fullscreen"
      className="fixed inset-0 z-[100] flex flex-col bg-background text-foreground animate-in fade-in duration-200 motion-reduce:animate-none"
    >
      <header className="mx-auto flex w-full max-w-5xl items-center gap-2 px-4 pt-[max(env(safe-area-inset-top),1rem)]">
        <p className="min-w-0 flex-1 truncate text-base font-extrabold tracking-tight sm:text-lg">
          {name || t('sessionPlan.today')}
        </p>
        {!runner.finished && runner.allTimed && !runner.flow && (
          <button
            type="button"
            onClick={runner.startFlow}
            className={cn(btn, 'h-10 border border-border px-3.5 hover:bg-muted')}
          >
            <Play className="size-3.5 fill-current" />
            <span className="hidden sm:inline">{t('sessionPlan.playFlow')}</span>
          </button>
        )}
        {!runner.finished && (
          <button
            type="button"
            onClick={runner.finish}
            className={cn(
              btn,
              'h-10 px-3.5',
              runner.remaining
                ? 'border border-border hover:bg-muted'
                : 'bg-primary text-primary-foreground hover:bg-primary/90',
            )}
          >
            <Check className="size-4" />
            {t('sessionPlan.finish')}
          </button>
        )}
        <button
          type="button"
          onClick={onMinimize}
          aria-label={t('common.close')}
          title={t('common.close')}
          className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
        >
          <ChevronDown className="size-5" />
        </button>
      </header>
      <div className="mx-auto mt-1 w-full max-w-5xl px-4">
        <p className="mb-1.5 flex justify-between gap-3 font-mono text-[11px] tabular-nums text-muted-foreground">
          <span className="truncate">
            {title && title !== name && `${title} · `}
            {clock(elapsed)}
          </span>
          <span className="shrink-0">{t('sessionPlan.progress', count)}</span>
        </p>
        <div className="h-1 overflow-hidden rounded-full bg-foreground/[0.08]" aria-hidden>
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none"
            style={{ width: `${count.total ? (count.done / count.total) * 100 : 0}%` }}
          />
        </div>
        {runner.rest && !runner.finished && <RestBar runner={runner} />}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {runner.finished ? (
          <Celebration
            name={name}
            prs={runner.finished.prs}
            elapsed={elapsed}
            debrief={debrief}
            voice={voice}
            onShare={
              sid
                ? () => {
                    onMinimize()
                    openShareCard({ type: 'session', ref: sid })
                  }
                : undefined
            }
            onDone={onMinimize}
          />
        ) : focused ? (
          <FocusView
            runner={runner}
            log={focused}
            onBack={() => setFocusId(null)}
            onNext={(id) => setFocusId(id)}
          />
        ) : (
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 p-4">
            {pep && (
              <div className="flex items-start gap-2.5" data-testid="coach-pep">
                <CoachAvatar voice={voice} size={36} className="shrink-0" />
                <p className="rounded-2xl rounded-tl-sm bg-muted px-3.5 py-2.5 text-sm leading-snug">
                  {pep}
                </p>
              </div>
            )}
            <BlockToggles runner={runner} view={view ?? undefined} />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((l) => (
                <ExerciseCard
                  key={l.itemId}
                  runner={runner}
                  log={l}
                  onOpen={() => setFocusId(l.itemId)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="mx-auto flex w-full max-w-xl flex-col items-center gap-2 px-4 pt-2 pb-[max(env(safe-area-inset-bottom),1rem)]">
        {runner.flow && runner.timer && <FlowBar runner={runner} />}
        <p className="text-center text-[11px] text-muted-foreground/80">
          {t('sessionPlan.safety')}
        </p>
      </footer>
    </div>,
    document.body,
  )
}

function SetDots({ log: l, className }: { log: ItemLog; className?: string }) {
  const { t } = useTranslation()
  const n = l.sets.filter((s) => s.done).length
  return (
    <span
      className={cn('flex items-center gap-1', className)}
      role="img"
      aria-label={t('sessionPlan.setsDone', { done: n, total: l.sets.length })}
    >
      {l.sets.map((s, i) => (
        <span
          key={i}
          className={cn(
            'size-2 rounded-full transition-colors',
            s.done ? 'bg-primary' : 'bg-foreground/15',
          )}
        />
      ))}
    </span>
  )
}

function ExerciseCard({
  runner,
  log: l,
  onOpen,
}: {
  runner: Runner
  log: ItemLog
  onOpen: () => void
}) {
  const { t, i18n } = useTranslation()
  const done = itemDone(l)
  const running = runner.timer?.itemId === l.itemId && runner.timer.phase !== 'next'
  const last = runner.lastOf(l.itemId)
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="exercise-card"
      className={cn(
        'group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border bg-card text-left transition-colors',
        done ? 'border-primary/40' : 'border-border/60 hover:border-border',
        running && 'ring-2 ring-primary/60',
      )}
    >
      <div className={cn('relative bg-muted/30 px-3 pt-3', done && 'opacity-60')}>
        <MoveFigure
          move={runner.moveOf(l)}
          kind={l.kind}
          animate={!done}
          className="mx-auto aspect-square w-full max-w-40 text-foreground"
        />
        {done && (
          <span className="absolute top-2 right-2 flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="size-3.5" strokeWidth={3} />
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        {l.block && (
          <span className="w-fit rounded-full bg-muted px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t(`sessionPlan.${l.block}`)}
          </span>
        )}
        <p className="line-clamp-2 text-sm leading-snug font-semibold">{l.name}</p>
        <p className="font-mono text-xs text-muted-foreground">
          {targetLine(t, logTarget(l), i18n.language)}
          {l.eachSide && ` · ${t('sessionPlan.eachSide')}`}
        </p>
        {last && (
          <p
            className="truncate font-mono text-[11px] text-muted-foreground/70"
            data-testid="last-time"
          >
            {lastLine(t, last, i18n.language)}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <SetDots log={l} />
          {running && runner.timer && (
            <span className="font-mono text-xs font-semibold tabular-nums text-primary-text">
              {clock(timerLeft(runner.timer, runner.now))}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

function FocusView({
  runner,
  log: l,
  onBack,
  onNext,
}: {
  runner: Runner
  log: ItemLog
  onBack: () => void
  onNext: (itemId: string) => void
}) {
  const { t, i18n } = useTranslation()
  const cue = runner.planItem(l.itemId)?.cue
  const last = runner.lastOf(l.itemId)
  const timer = runner.timer?.itemId === l.itemId ? runner.timer : null
  const items = runner.items
  const at = items.findIndex((x) => x.itemId === l.itemId)
  const next =
    items.slice(at + 1).find((x) => !itemDone(x)) ??
    items.slice(0, at).find((x) => !itemDone(x)) ??
    null
  const label = timer ? phaseLabel(t, timer, items) : null
  const left = timer ? timerLeft(timer, runner.now) : 0
  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-4 p-4" data-testid="exercise-focus">
      <button
        type="button"
        onClick={onBack}
        className="flex w-fit cursor-pointer items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" />
        {t('sessionPlan.allExercises')}
      </button>
      <div className="relative mx-auto w-[min(70vw,34vh,300px)]">
        <MoveFigure
          move={runner.moveOf(l)}
          kind={l.kind}
          animate={!itemDone(l)}
          className={cn(
            'aspect-square w-full text-foreground transition-transform duration-500 motion-reduce:transition-none',
            timer?.phase === 'right' && '-scale-x-100',
          )}
        />
      </div>
      <div className="text-center">
        <h2 className="text-2xl font-extrabold tracking-tight">{l.name}</h2>
        <p className="mt-1 font-mono text-sm text-muted-foreground">
          {targetLine(t, logTarget(l), i18n.language)}
          {l.eachSide && ` · ${t('sessionPlan.eachSide')}`}
        </p>
        {last && (
          <p className="mt-0.5 font-mono text-xs text-muted-foreground/70">
            {lastLine(t, last, i18n.language)}
          </p>
        )}
        {cue && <p className="mt-1 text-sm text-muted-foreground">{cue}</p>}
        <SetDots log={l} className="mt-2 justify-center" />
      </div>
      {runner.rest && !timer && <RestPanel runner={runner} />}
      {timer && (
        <div className="flex flex-col items-center gap-1" aria-live="polite">
          <div className="flex items-center gap-3">
            <div className="scale-150">
              <TimerRing ratio={1 - left / phaseMs(timer, l)} />
            </div>
            <span className="font-mono text-5xl font-black tabular-nums" role="timer">
              {clock(left)}
            </span>
          </div>
          {label && <p className="text-sm font-semibold text-muted-foreground">{label}</p>}
        </div>
      )}
      <SetRows runner={runner} log={l} large />
      {next && (
        <button
          type="button"
          onClick={() => onNext(next.itemId)}
          className={cn(btn, 'h-12 w-full border border-border hover:bg-muted')}
        >
          {t('sessionPlan.nextExercise')}
          <span className="max-w-[50%] truncate font-normal text-muted-foreground">
            {next.name}
          </span>
          <ArrowRight className="size-4" />
        </button>
      )}
    </div>
  )
}

function FlowBar({ runner }: { runner: Runner }) {
  const { t } = useTranslation()
  const paused = runner.timer?.paused !== undefined
  return (
    <div className="flex w-full gap-2" data-testid="flow-bar">
      <button
        type="button"
        onClick={runner.togglePause}
        className={cn(btn, 'h-12 flex-1 border border-border hover:bg-muted')}
      >
        {paused ? <Play className="size-4" /> : <Pause className="size-4" />}
        {t(paused ? 'sessionPlan.resume' : 'sessionPlan.pause')}
      </button>
      <button
        type="button"
        onClick={runner.skip}
        className={cn(btn, 'h-12 flex-1 border border-border hover:bg-muted')}
      >
        <SkipForward className="size-4" />
        {t('sessionPlan.skip')}
      </button>
      <button
        type="button"
        onClick={runner.stopTimer}
        aria-label={t('sessionPlan.stopFlow')}
        title={t('sessionPlan.stopFlow')}
        className={cn(btn, 'size-12 shrink-0 border border-border hover:bg-muted')}
      >
        <Square className="size-4" />
      </button>
    </div>
  )
}

function Celebration({
  name,
  prs,
  elapsed,
  debrief,
  voice,
  onShare,
  onDone,
}: {
  name: string
  prs: string[]
  elapsed: number
  debrief: string | null
  voice: string | undefined
  onShare?: () => void
  onDone: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      className="mx-auto flex min-h-full w-full max-w-md flex-col items-center justify-center gap-4 p-6 text-center"
      data-testid="session-done"
    >
      <span className="flex size-24 items-center justify-center rounded-full bg-primary text-primary-foreground animate-in zoom-in-50 duration-500 motion-reduce:animate-none">
        <Check className="size-12" strokeWidth={3} />
      </span>
      <div>
        <h2 className="text-3xl font-black tracking-tight">{t('sessionPlan.sessionDone')}</h2>
        <p className="mt-1 font-mono text-sm text-muted-foreground">
          {name && `${name} · `}
          {clock(elapsed)}
        </p>
      </div>
      {prs.length > 0 && (
        <ul className="flex w-full flex-col gap-1.5">
          {prs.map((p) => (
            <li
              key={p}
              className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-left text-sm font-semibold"
            >
              <TrendingUp className="size-4 shrink-0 text-primary-text" strokeWidth={2.5} />
              {t('sessionPlan.newBest', { what: p })}
            </li>
          ))}
        </ul>
      )}
      <div
        className="flex min-h-14 w-full items-start gap-2.5 text-left"
        data-testid="coach-debrief"
      >
        <CoachAvatar voice={voice} size={36} className="shrink-0" />
        <p
          className={cn(
            'rounded-2xl rounded-tl-sm bg-muted px-3.5 py-2.5 text-sm leading-snug',
            !debrief && 'animate-pulse text-muted-foreground',
          )}
        >
          {debrief ?? t('sessionPlan.debriefPending')}
        </p>
      </div>
      <div className="flex w-full gap-2">
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            className={cn(btn, 'h-12 flex-1 border border-border hover:bg-muted')}
          >
            <Share2 className="size-4" />
            {t('sessionPlan.share')}
          </button>
        )}
        <button
          type="button"
          onClick={onDone}
          className={cn(btn, 'h-12 flex-1 bg-primary text-primary-foreground hover:bg-primary/90')}
        >
          {t('sessionPlan.done')}
        </button>
      </div>
    </div>
  )
}

function RestBar({ runner }: { runner: Runner }) {
  const { t } = useTranslation()
  const rest = runner.rest!
  const left = Math.max(0, rest.endsAt - runner.now)
  return (
    <div className="mt-2 flex items-center gap-2" data-testid="rest-bar" aria-live="polite">
      <span className="font-mono text-xs font-semibold tabular-nums">
        {t('sessionPlan.restFor', { time: clock(left) })}
      </span>
      <div className="h-1 flex-1 overflow-hidden rounded-full bg-foreground/[0.08]" aria-hidden>
        <div
          className="h-full rounded-full bg-primary/70 transition-[width] duration-300 ease-linear motion-reduce:transition-none"
          style={{ width: `${(left / rest.total) * 100}%` }}
        />
      </div>
      <button
        type="button"
        onClick={() => runner.addRest()}
        className="cursor-pointer rounded-full px-2 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        {t('sessionPlan.restMore')}
      </button>
      <button
        type="button"
        onClick={runner.skipRest}
        className="cursor-pointer rounded-full px-2 py-0.5 text-[11px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        {t('sessionPlan.restSkip')}
      </button>
    </div>
  )
}

function RestPanel({ runner }: { runner: Runner }) {
  const { t } = useTranslation()
  const rest = runner.rest!
  const left = Math.max(0, rest.endsAt - runner.now)
  return (
    <div
      className="flex flex-col items-center gap-2 rounded-2xl border border-border/60 bg-muted/30 p-4"
      data-testid="rest-panel"
    >
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {t('sessionPlan.rest')}
      </p>
      <div className="flex items-center gap-3">
        <div className="scale-125">
          <TimerRing ratio={1 - left / rest.total} />
        </div>
        <span className="font-mono text-4xl font-black tabular-nums" role="timer">
          {clock(left)}
        </span>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => runner.addRest()}
          className={cn(btn, 'h-10 border border-border px-4 hover:bg-muted')}
        >
          {t('sessionPlan.restMore')}
        </button>
        <button
          type="button"
          onClick={runner.skipRest}
          className={cn(btn, 'h-10 border border-border px-4 hover:bg-muted')}
        >
          <SkipForward className="size-4" />
          {t('sessionPlan.restSkip')}
        </button>
      </div>
    </div>
  )
}

// "Skip warm-up" / "Add warm-up back" (and cool-down) for today's session.
function BlockToggles({
  runner,
  view,
}: {
  runner: Runner
  view: { plan: { sessions: { id: string; items: { block?: Block }[] }[] } | null } | undefined
}) {
  const { t } = useTranslation()
  const setBlock = useMutation(api.sessionPlans.setBlock)
  const session = runner.session
  if (!session || !view?.plan) return null
  const planned = view.plan.sessions.find((s) => s.id === session.sessionId)?.items ?? []
  const blocks = (['warmup', 'cooldown'] as const).filter((b) => planned.some((i) => i.block === b))
  if (!blocks.length) return null
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="block-toggles">
      {blocks.map((b) => {
        const on = session.items.some((l) => l.block === b)
        return (
          <button
            key={b}
            type="button"
            onClick={() => void setBlock({ sessionId: session._id, block: b, on: !on })}
            className="cursor-pointer rounded-full border border-border/70 px-3 py-1 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {t(on ? `sessionPlan.skip_${b}` : `sessionPlan.add_${b}`)}
          </button>
        )
      })}
    </div>
  )
}
